import { useState, useEffect, RefObject } from 'react';

interface ElementSize {
  width: number;
  height: number;
}

interface UseElementSizeOptions {
  widthOnly?: boolean;
  debounceDelay?: number;
}

export const useElementSize = (
  elementRef: RefObject<HTMLElement | null>,
  options: UseElementSizeOptions = {},
): ElementSize => {
  const { widthOnly = false, debounceDelay = 0 } = options;

  const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 });

  useEffect(() => {
    const element = elementRef.current;
    if (!element) {
      return;
    }

    let timeoutId: NodeJS.Timeout;

    const updateSize = (entries: ResizeObserverEntry[]) => {
      if (entries.length === 0) {
        return;
      }

      const { width, height } = entries[0].contentRect;

      const newSize = widthOnly
        ? { width: Math.round(width), height: size.height }
        : { width: Math.round(width), height: Math.round(height) };

      if (debounceDelay > 0) {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => setSize(newSize), debounceDelay);
      } else {
        setSize(newSize);
      }
    };

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(updateSize);
      resizeObserver.observe(element);

      const { offsetWidth, offsetHeight } = element;
      const initialSize = widthOnly
        ? { width: offsetWidth, height: size.height }
        : { width: offsetWidth, height: offsetHeight };
      setSize(initialSize);

      return () => {
        clearTimeout(timeoutId);
        resizeObserver.disconnect();
      };
    } else {
      // Fallback for older browsers
      const handleResize = () => {
        const { offsetWidth, offsetHeight } = element;
        const newSize = widthOnly
          ? { width: offsetWidth, height: size.height }
          : { width: offsetWidth, height: offsetHeight };

        if (debounceDelay > 0) {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => setSize(newSize), debounceDelay);
        } else {
          setSize(newSize);
        }
      };

      handleResize();
      window.addEventListener('resize', handleResize);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [elementRef, widthOnly, debounceDelay, size.height]);

  return size;
};
